import {
  Portal as RadixPortal,
  type PortalProps as RadixPortalProps,
} from '@radix-ui/react-portal'
import { Fragment, type FC } from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { useModalDialogContext } from '../ModalDialogContext'

type PortalProps = {
  enabled?: boolean
  container?: RadixPortalProps['container']
}

export const Portal: FC<React.PropsWithChildren<PortalProps>> = ({
  children,
  container: providedContainer,
  enabled = true,
}) => {
  const theme = useTheme()
  /**
   * When this portal is used within a Radix dialog, we need to mount
   * the portal inside the Radix dialog's portal, to ensure that our
   * child component(s) are not interfered with by Radix's focus scope.
   */
  const modalRef = useModalDialogContext()

  if (!enabled) {
    return <Fragment>{children}</Fragment>
  }

  /**
   * RadixPortal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <RadixPortal
      asChild
      container={providedContainer ?? modalRef.current ?? document.body}
    >
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </RadixPortal>
  )
}
Portal.displayName = 'Lapidary: Portal'
